import { actionColors } from "../themeColorTokens";
import { brand } from "../theme/brand";
import { isNative, isNativeIOS } from "../lib/platform";

type Props = {
  onCreateAccount: () => void;
  onSignIn: () => void;
  onTryDemo: () => void;
  onContinueLocally: () => void;
};

// ─── Colors ───────────────────────────────────────────────────────────────────
const PURPLE = "#8b5cf6";           // plan dot on progress bar (purple — keep)
const AHEAD_DOT = brand.state.success; // "now" dot on progress bar
const CARD_BG = "rgba(255,255,255,0.05)";
const CARD_BORDER = brand.border.subtle;
const LIGHT_BLUE = brand.chart.trend;
const BRAND_GREEN = brand.accent.brand;

// ─── Static demo data (cut_ahead profile, 2026-03-10) ────────────────────────
// Sprint: Feb 08 → Jun 01, 170 → 160 lbs ("Summer Cut")
// Day 30 of ~114 — plan = 166.0, actual = 163.0 → 3.0 lb ahead
// Bar positions: plan at 40%, actual at 70% (start→goal axis, cut direction)
const DEMO = {
  sprintName: "Summer Cut",
  pillLabel: "Ahead",
  pillBg: "rgba(34,197,94,0.14)",
  modeBg: "rgba(59,130,246,0.20)",
  displayWeight: "163.0",
  sourceSubtext: "5-day avg • Mar 10",
  secondarySubtext: "Last log 163.0 • Mar 10",
  alignCopy: "▲ 3.0 lb ahead",
  alignColor: "#86efac",
  temporalStatus: "12 days early",
  temporalColor: "#22c55e",
  startWeightLabel: "170.0 lb",
  startDateLabel: "Feb 08",
  endWeightLabel: "160 lb",
  endDateLabel: "Jun 01",
  plannedPct: 40,
  actualPct: 70,
  gapStartPct: 40,
  gapWidthPct: 30,
  planLabel: "Plan 166.0",
  nowLabel: "Now 163.0",
  // KPIs
  netKpi: "−7.0 lb",
  rateKpi: "−1.6 lb/wk",
  targetKpi: "−500 net/day",
  leftKpi: "30d",
  // Mobile mini-chips
  netChangeChip: "−7.0 lb net",
  lbsToGoChip: "3.0 lb to go",
  daysLeftChip: "30 days left",
};

// ─── Shared sub-components ────────────────────────────────────────────────────

function DemoPill({ label, bg }: { label: string; bg: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", padding: "0.35rem 0.7rem", borderRadius: 999, background: bg, border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.92)", fontSize: "0.85rem", fontWeight: 700, lineHeight: 1.15 }}>
      {label}
    </span>
  );
}

function KpiCard({ label, value, tone }: { label: string; value: string; tone: "green" | "muted" }) {
  const toneStyles = {
    green: { border: "1px solid rgba(34,197,94,0.36)", background: "rgba(34,197,94,0.14)", color: "#bbf7d0" },
    muted: { border: "1px solid rgba(255,255,255,0.16)", background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.92)" },
  };
  return (
    <div style={{ ...toneStyles[tone], borderRadius: 12, padding: "0.5rem 0.64rem", minWidth: 100 }}>
      <div style={{ fontSize: "0.6rem", letterSpacing: "0.09em", textTransform: "uppercase", color: "rgba(255,255,255,0.62)", fontWeight: 700 }}>{label}</div>
      <div style={{ marginTop: "0.15rem", fontSize: "0.96rem", fontWeight: 900, color: toneStyles[tone].color }}>{value}</div>
    </div>
  );
}

function ProgressBar() {
  return (
    <div style={{ position: "relative", height: 17, borderRadius: 999, background: "linear-gradient(90deg, rgba(148,163,184,0.2), rgba(148,163,184,0.28))", overflow: "visible" }}>
      {/* Green gap between plan and actual */}
      <div style={{ position: "absolute", top: 2, bottom: 2, left: `${DEMO.gapStartPct}%`, width: `${DEMO.gapWidthPct}%`, borderRadius: 6, background: "linear-gradient(90deg, rgba(74,222,128,0.34), rgba(74,222,128,0.52))" }} />
      {/* Plan dot — purple outline */}
      <div style={{ position: "absolute", top: "50%", left: `${DEMO.plannedPct}%`, transform: "translate(-50%, -50%)", width: 14, height: 14, borderRadius: 999, border: `2.2px solid ${PURPLE}`, background: "rgba(15,23,42,0.95)" }} />
      {/* Now dot — green filled */}
      <div style={{ position: "absolute", top: "50%", left: `${DEMO.actualPct}%`, transform: "translate(-50%, -50%)", width: 16, height: 16, borderRadius: 999, background: AHEAD_DOT, boxShadow: `0 0 7px ${AHEAD_DOT}99` }} />
    </div>
  );
}

// Plan/Now floating labels — desktop (no overlap, both at top 8)
function ProgressLabelsDesktop() {
  return (
    <div style={{ position: "relative", height: 24, marginTop: "0.02rem" }}>
      <div style={{ position: "absolute", left: "clamp(6px, calc(40% - 41px), calc(100% - 88px))", top: 8, display: "inline-flex", alignItems: "center", gap: "0.25rem", fontSize: "0.62rem", color: "rgba(255,255,255,0.75)", whiteSpace: "nowrap" }}>
        <span style={{ width: 7, height: 7, borderRadius: 999, border: `1.4px solid ${PURPLE}`, background: "transparent", flexShrink: 0 }} />
        <span>{DEMO.planLabel}</span>
      </div>
      <div style={{ position: "absolute", left: "clamp(6px, calc(70% - 38px), calc(100% - 82px))", top: 8, display: "inline-flex", alignItems: "center", gap: "0.25rem", fontSize: "0.62rem", color: "rgba(255,255,255,0.84)", whiteSpace: "nowrap" }}>
        <span style={{ width: 7, height: 7, borderRadius: 999, background: AHEAD_DOT, flexShrink: 0 }} />
        <span>{DEMO.nowLabel}</span>
      </div>
    </div>
  );
}

// Plan/Now floating labels — mobile (same row, dots far enough apart to avoid overlap)
function ProgressLabelsMobile() {
  return (
    <div style={{ position: "relative", height: 24, marginTop: "0.08rem" }}>
      <div style={{ position: "absolute", left: "clamp(6px, calc(40% - 41px), calc(100% - 88px))", top: 2, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.25rem", fontSize: "0.6rem", color: "rgba(255,255,255,0.75)", whiteSpace: "nowrap", maxWidth: 124 }}>
        <span style={{ width: 7, height: 7, borderRadius: 999, border: `1.4px solid ${PURPLE}`, background: "transparent", flexShrink: 0 }} />
        <span>{DEMO.planLabel}</span>
      </div>
      <div style={{ position: "absolute", left: "clamp(6px, calc(70% - 38px), calc(100% - 82px))", top: 2, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.25rem", fontSize: "0.6rem", color: "rgba(255,255,255,0.84)", whiteSpace: "nowrap", maxWidth: 124 }}>
        <span style={{ width: 7, height: 7, borderRadius: 999, background: AHEAD_DOT, flexShrink: 0 }} />
        <span>{DEMO.nowLabel}</span>
      </div>
    </div>
  );
}

// ─── Desktop sprint status card ───────────────────────────────────────────────
// Matches the non-isDashboardMobile branch of sprintStatusPanel in WeightTracker
function SprintCardDesktop() {
  return (
    <div style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}`, borderRadius: 18, padding: "1.05rem 1.15rem" }}>
      <div style={{ display: "grid", gridTemplateColumns: "120px minmax(360px,1fr)", gridTemplateRows: "auto auto", columnGap: 18, rowGap: "0.55rem", alignItems: "start" }}>

        {/* Row 1: header — full width */}
        <div style={{ gridColumn: "1 / span 2", gridRow: 1, display: "flex", alignItems: "baseline", gap: "0.55rem", flexWrap: "wrap" }}>
          <div style={{ fontSize: "1.26rem", fontWeight: 900, minWidth: 0 }}>
            Sprint Status —{" "}
            <span style={{ display: "inline-block", maxWidth: 340, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "bottom" }}>
              {DEMO.sprintName}
            </span>
          </div>
          <DemoPill label={DEMO.pillLabel} bg={DEMO.pillBg} />
          <DemoPill label="Cut" bg={DEMO.modeBg} />
        </div>

        {/* Row 2, Col 1: "You are here" weight */}
        <div style={{ gridColumn: 1, gridRow: 2 }}>
          <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.48)", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>You are here</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: "0.35rem", marginTop: "0.42rem" }}>
            <span style={{ fontSize: "2.05rem", fontWeight: 860, lineHeight: 1, color: "white" }}>{DEMO.displayWeight}</span>
            <span style={{ fontSize: "0.9rem", fontWeight: 700, color: LIGHT_BLUE }}>lbs</span>
          </div>
        </div>

        {/* Row 2, Col 2: progress bar + KPIs */}
        <div style={{ gridColumn: 2, gridRow: 2, borderLeft: "1px solid rgba(255,255,255,0.08)", paddingLeft: "1rem" }}>

          {/* Start badge | bar area | Goal badge */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.62rem" }}>
            <div style={{ display: "inline-flex", flexDirection: "column", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)", borderRadius: 6, padding: "0.1rem 0.28rem", lineHeight: 1.05 }}>
              <div style={{ fontSize: 10, lineHeight: "11px", color: "rgba(255,255,255,0.55)", fontWeight: 700 }}>Start</div>
              <div style={{ fontSize: 11, lineHeight: "12px", color: "rgba(255,255,255,0.75)", fontWeight: 600 }}>{DEMO.startWeightLabel}</div>
              <div style={{ fontSize: 10, lineHeight: "11px", color: "rgba(255,255,255,0.55)", fontWeight: 600 }}>{DEMO.startDateLabel}</div>
            </div>

            <div style={{ flex: 1, minWidth: 0, display: "grid", gap: "0.16rem" }}>
              <div>
                <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.48)", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>Plan vs Actual</div>
                <div style={{ marginTop: "0.2rem", fontSize: "0.84rem", fontWeight: 700, color: DEMO.alignColor, display: "flex", alignItems: "center", gap: "0.34rem", flexWrap: "wrap" }}>
                  <span>{DEMO.alignCopy}</span>
                  <span style={{ color: "rgba(255,255,255,0.36)", fontWeight: 600 }}>•</span>
                  <span style={{ color: DEMO.temporalColor }}>{DEMO.temporalStatus}</span>
                </div>
              </div>
              <ProgressBar />
              <ProgressLabelsDesktop />
            </div>

            <div style={{ display: "inline-flex", flexDirection: "column", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)", borderRadius: 6, padding: "0.1rem 0.28rem", lineHeight: 1.05, textAlign: "right" }}>
              <div style={{ fontSize: 10, lineHeight: "11px", color: "rgba(255,255,255,0.55)", fontWeight: 700 }}>Goal</div>
              <div style={{ fontSize: 11, lineHeight: "12px", color: "rgba(255,255,255,0.75)", fontWeight: 600 }}>{DEMO.endWeightLabel}</div>
              <div style={{ fontSize: 10, lineHeight: "11px", color: "rgba(255,255,255,0.55)", fontWeight: 600 }}>{DEMO.endDateLabel}</div>
            </div>
          </div>

          {/* KPI chips */}
          <div style={{ marginTop: "0.6rem", display: "flex", gap: "0.55rem", flexWrap: "wrap", justifyContent: "center" }}>
            <KpiCard label="NET CHANGE" value={DEMO.netKpi} tone="green" />
            <KpiCard label="PACE" value={DEMO.rateKpi} tone="green" />
            <KpiCard label="TARGET" value={DEMO.targetKpi} tone="muted" />
            <KpiCard label="LEFT" value={DEMO.leftKpi} tone="muted" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Mobile sprint status card (expanded) ────────────────────────────────────
// Matches the isDashboardMobile + sprintStatusExpanded branch in WeightTracker
function SprintCardMobile() {
  const kpiCardMini = (value: string) => (
    <div style={{ border: "1px solid rgba(255,255,255,0.16)", background: "rgba(255,255,255,0.04)", borderRadius: 12, padding: "0.26rem 0.46rem", textAlign: "center" }}>
      <div style={{ fontSize: "0.67rem", fontWeight: 700, color: "rgba(255,255,255,0.82)", whiteSpace: "nowrap" }}>{value}</div>
    </div>
  );

  return (
    <div style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}`, borderRadius: 18, padding: "0.8rem 0.85rem", width: "100%", maxWidth: 420, margin: "0 auto" }}>

      {/* Header */}
      <div style={{ fontSize: "1rem", fontWeight: 900 }}>
        Sprint Status —{" "}
        <span style={{ display: "inline-block", maxWidth: "min(220px, calc(100vw - 12rem))", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "bottom" }}>
          {DEMO.sprintName}
        </span>
      </div>

      {/* Pills */}
      <div style={{ marginTop: "0.42rem", display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
        <DemoPill label={DEMO.pillLabel} bg={DEMO.pillBg} />
        <DemoPill label="Cut" bg={DEMO.modeBg} />
      </div>

      {/* Weight */}
      <div style={{ marginTop: "0.62rem" }}>
        <div style={{ fontSize: "1.92rem", fontWeight: 860, color: "white", lineHeight: 1, whiteSpace: "nowrap" }}>
          {DEMO.displayWeight} <span style={{ fontSize: "0.9rem", color: LIGHT_BLUE }}>lb</span>
        </div>
        <div style={{ marginTop: "0.26rem", fontSize: "0.74rem", color: "rgba(255,255,255,0.74)" }}>
          Target {DEMO.endWeightLabel} by {DEMO.endDateLabel}
        </div>
        <div style={{ marginTop: "0.45rem", fontSize: "0.84rem", fontWeight: 700, color: DEMO.alignColor, display: "flex", alignItems: "center", gap: "0.34rem", flexWrap: "wrap" }}>
          <span>{DEMO.alignCopy}</span>
          <span style={{ color: "rgba(255,255,255,0.36)", fontWeight: 600 }}>•</span>
          <span style={{ color: DEMO.temporalColor }}>{DEMO.temporalStatus}</span>
        </div>
      </div>

      {/* Plan vs Actual */}
      <div style={{ marginTop: "0.66rem", borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "0.62rem" }}>
        <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: "0.09em", fontWeight: 700 }}>Plan vs Actual</div>
        <div style={{ marginTop: "0.5rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
          <div>
            <div style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.58)", fontWeight: 700 }}>Start</div>
            <div style={{ marginTop: "0.14rem", fontSize: "0.88rem", fontWeight: 800 }}>{DEMO.startWeightLabel}</div>
            <div style={{ marginTop: "0.08rem", fontSize: "0.66rem", color: "rgba(255,255,255,0.58)" }}>{DEMO.startDateLabel}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.58)", fontWeight: 700 }}>Goal</div>
            <div style={{ marginTop: "0.14rem", fontSize: "0.88rem", fontWeight: 800 }}>{DEMO.endWeightLabel}</div>
            <div style={{ marginTop: "0.08rem", fontSize: "0.66rem", color: "rgba(255,255,255,0.58)" }}>{DEMO.endDateLabel}</div>
          </div>
        </div>
        <div style={{ marginTop: "0.5rem", position: "relative", height: 18, borderRadius: 999, background: "linear-gradient(90deg, rgba(148,163,184,0.2), rgba(148,163,184,0.28))" }}>
          <div style={{ position: "absolute", top: 2, bottom: 2, left: `${DEMO.gapStartPct}%`, width: `${DEMO.gapWidthPct}%`, borderRadius: 6, background: "linear-gradient(90deg, rgba(74,222,128,0.34), rgba(74,222,128,0.52))" }} />
          <div style={{ position: "absolute", top: "50%", left: `${DEMO.plannedPct}%`, transform: "translate(-50%, -50%)", width: 14, height: 14, borderRadius: 999, border: `2.2px solid ${PURPLE}`, background: "rgba(15,23,42,0.95)" }} />
          <div style={{ position: "absolute", top: "50%", left: `${DEMO.actualPct}%`, transform: "translate(-50%, -50%)", width: 16, height: 16, borderRadius: 999, background: AHEAD_DOT, boxShadow: `0 0 7px ${AHEAD_DOT}99` }} />
        </div>
        <ProgressLabelsMobile />
      </div>

      {/* Sprint Metrics */}
      <div style={{ marginTop: "0.66rem", borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "0.62rem" }}>
        <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: "0.09em", fontWeight: 700 }}>Sprint Metrics</div>
        <div style={{ marginTop: "0.45rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
          <div style={{ border: "1px solid rgba(34,197,94,0.36)", background: "rgba(34,197,94,0.14)", borderRadius: 12, padding: "0.42rem 0.58rem", minWidth: 0 }}>
            <div style={{ fontSize: "0.6rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.62)", fontWeight: 700 }}>NET CHANGE</div>
            <div style={{ marginTop: "0.12rem", fontSize: "0.92rem", fontWeight: 900, color: "#bbf7d0" }}>{DEMO.netKpi}</div>
          </div>
          <div style={{ border: "1px solid rgba(34,197,94,0.36)", background: "rgba(34,197,94,0.14)", borderRadius: 12, padding: "0.42rem 0.58rem", minWidth: 0 }}>
            <div style={{ fontSize: "0.6rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.62)", fontWeight: 700 }}>AVG RATE</div>
            <div style={{ marginTop: "0.12rem", fontSize: "0.92rem", fontWeight: 900, color: "#bbf7d0" }}>{DEMO.rateKpi}</div>
          </div>
          <div style={{ border: "1px solid rgba(255,255,255,0.16)", background: "rgba(255,255,255,0.04)", borderRadius: 12, padding: "0.42rem 0.58rem", minWidth: 0 }}>
            <div style={{ fontSize: "0.6rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.62)", fontWeight: 700 }}>TARGET CAL</div>
            <div style={{ marginTop: "0.12rem", fontSize: "0.82rem", fontWeight: 800, color: "rgba(255,255,255,0.92)" }}>{DEMO.targetKpi}</div>
          </div>
          <div style={{ border: "1px solid rgba(255,255,255,0.16)", background: "rgba(255,255,255,0.04)", borderRadius: 12, padding: "0.42rem 0.58rem", minWidth: 0 }}>
            <div style={{ fontSize: "0.6rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.62)", fontWeight: 700 }}>LEFT</div>
            <div style={{ marginTop: "0.12rem", fontSize: "0.92rem", fontWeight: 900, color: "rgba(255,255,255,0.92)" }}>{DEMO.leftKpi}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Ghost progress chart (iOS watermark) ─────────────────────────────────────
// Decorative only — stock-like downward trend chart watermark for iOS hero
function GhostProgressChart() {
  return (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: "54%",
        pointerEvents: "none",
        userSelect: "none",
      }}
    >
      <svg
        viewBox="0 0 400 480"
        preserveAspectRatio="xMidYMid slice"
        style={{ width: "100%", height: "100%", opacity: 0.55 }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="ghost-chart-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(132,204,22,0.92)" />
            <stop offset="100%" stopColor="rgba(9,16,11,0.05)" />
          </linearGradient>
          {/* Gradient mask: grid lines fade to invisible toward the top (before Try Demo) */}
          <linearGradient id="grid-fade" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="white" stopOpacity="0" />
            <stop offset="22%" stopColor="white" stopOpacity="0" />
            <stop offset="48%" stopColor="white" stopOpacity="1" />
            <stop offset="100%" stopColor="white" stopOpacity="1" />
          </linearGradient>
          <mask id="grid-mask">
            <rect x="0" y="0" width="400" height="480" fill="url(#grid-fade)" />
          </mask>
        </defs>
        {/* Vertical grid lines — 5 lines at 70px spacing, fade via mask */}
        <g mask="url(#grid-mask)">
          <line x1="70"  y1="0" x2="70"  y2="480" stroke={BRAND_GREEN} strokeOpacity="0.22" strokeWidth="1.0" />
          <line x1="140" y1="0" x2="140" y2="480" stroke={BRAND_GREEN} strokeOpacity="0.22" strokeWidth="1.0" />
          <line x1="210" y1="0" x2="210" y2="480" stroke={BRAND_GREEN} strokeOpacity="0.22" strokeWidth="1.0" />
          <line x1="280" y1="0" x2="280" y2="480" stroke={BRAND_GREEN} strokeOpacity="0.22" strokeWidth="1.0" />
          <line x1="350" y1="0" x2="350" y2="480" stroke={BRAND_GREEN} strokeOpacity="0.22" strokeWidth="1.0" />
        </g>
        {/* Horizontal grid lines — full chart height, fade to transparent near top */}
        <g mask="url(#grid-mask)">
          <line x1="0" y1="80"  x2="400" y2="80"  stroke={BRAND_GREEN} strokeOpacity="0.24" strokeWidth="1.0" />
          <line x1="0" y1="160" x2="400" y2="160" stroke={BRAND_GREEN} strokeOpacity="0.24" strokeWidth="1.0" />
          <line x1="0" y1="240" x2="400" y2="240" stroke={BRAND_GREEN} strokeOpacity="0.24" strokeWidth="1.0" />
          <line x1="0" y1="320" x2="400" y2="320" stroke={BRAND_GREEN} strokeOpacity="0.24" strokeWidth="1.0" />
          <line x1="0" y1="400" x2="400" y2="400" stroke={BRAND_GREEN} strokeOpacity="0.24" strokeWidth="1.0" />
        </g>
        {/* Weight trend curve — loses more up front, gradually levels out */}
        <path
          d="M0,138 L20,163 L38,178 L51,171 L65,188 L80,198 L91,195 L104,207 L117,216 L129,212 L142,221 L155,230 L166,236 L177,241 L189,247 L200,244 L213,251 L224,255 L235,253 L247,258 L259,263 L272,268 L285,272 L298,276 L312,281 L326,285 L338,291 L348,296 L356,300 L364,305 L364,480 L0,480 Z"
          fill="url(#ghost-chart-fill)"
        />
        <path
          d="M0,138 L20,163 L38,178 L51,171 L65,188 L80,198 L91,195 L104,207 L117,216 L129,212 L142,221 L155,230 L166,236 L177,241 L189,247 L200,244 L213,251 L224,255 L235,253 L247,258 L259,263 L272,268 L285,272 L298,276 L312,281 L326,285 L338,291 L348,296 L356,300 L364,305"
          fill="none"
          stroke={BRAND_GREEN}
          strokeWidth="3.5"
          strokeOpacity="1.0"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Circular crosshair target marker at line end (85% x-axis) */}
        <g transform="translate(364 305)">
          <circle cx="0" cy="0" r="10" fill="rgba(132,204,22,0.1)" stroke={BRAND_GREEN} strokeOpacity="0.96" strokeWidth="1.8" />
          <circle cx="0" cy="0" r="5" fill="none" stroke={BRAND_GREEN} strokeOpacity="0.94" strokeWidth="1.5" />
          <circle cx="0" cy="0" r="2.2" fill={BRAND_GREEN} fillOpacity="0.95" />
          <line x1="-10" y1="0" x2="10" y2="0" stroke={BRAND_GREEN} strokeOpacity="0.94" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="0" y1="-10" x2="0" y2="10" stroke={BRAND_GREEN} strokeOpacity="0.94" strokeWidth="1.5" strokeLinecap="round" />
        </g>
      </svg>
    </div>
  );
}

// ─── Landing page ─────────────────────────────────────────────────────────────

export function LandingPage({ onCreateAccount, onSignIn, onTryDemo, onContinueLocally }: Props) {
  return (
    <main
      style={{
        minHeight: "100vh",
        height: "100dvh",
        overflowY: "auto",
        overflowX: "hidden",
        WebkitOverflowScrolling: "touch",
        overscrollBehaviorY: "contain",
        position: "relative",
        color: "#f4f4f5",
        background: brand.background.landing,
        fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial',
      }}
    >
      {/* ── Hero (all platforms) ─────────────────────────────────────────── */}
      <section
        className="landing-hero"
        style={{
          maxWidth: 1152,
          margin: "0 auto",
          padding: isNative ? "92px 24px 72px" : "96px 24px 80px",
          textAlign: "center",
        }}
      >
        <div className="landing-logo-wrap" style={{ margin: "0 auto 24px", width: 220, height: 220, overflow: "hidden" }}>
          <img
            src="/Logo-gt.png"
            alt="WeightPlanner Pro"
            style={{ width: "100%", height: "100%", display: "block", objectFit: "contain", transform: "scale(1.45)", transformOrigin: "center" }}
          />
        </div>

        {!isNative && (
          <p style={{ margin: "0 0 12px", fontSize: "0.875rem", fontWeight: 500, color: "#71717a", letterSpacing: "0.04em" }}>
            WeightPlanner Pro
          </p>
        )}

        <h1 className="landing-hero-title" style={{ fontSize: "clamp(3rem, 6vw, 3.75rem)", fontWeight: 600, letterSpacing: "-0.025em", lineHeight: 1.05, margin: 0 }}>
          Hit your weight goal. Every time.
        </h1>

        <p className="landing-hero-copy" style={{ margin: "24px auto 0", maxWidth: 672, fontSize: "1.18rem", fontWeight: 450, lineHeight: 1.55, color: "#a1a1aa" }}>
          A weekly-adjusting plan, built around how you actually train and live. Get a daily calorie target that adapts as your body responds.
        </p>

        <div className="landing-hero-cta" style={{ marginTop: 40, display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={onContinueLocally}
            style={{
              background: `linear-gradient(135deg, ${brand.accent.brand} 0%, ${brand.accent.primary} 100%)`,
              color: "#0f172a",
              border: "1px solid rgba(148,163,184,0.45)",
              borderRadius: 12,
              padding: "12px 32px",
              fontSize: "1.125rem",
              fontWeight: 700,
              cursor: "pointer",
              minHeight: 56,
            }}
          >
            Build my plan
          </button>
          <button
            type="button"
            onClick={onSignIn}
            style={{
              background: "transparent",
              color: "#f4f4f5",
              border: "1px solid #3f3f46",
              borderRadius: 12,
              padding: "12px 32px",
              fontSize: "1.125rem",
              fontWeight: 500,
              cursor: "pointer",
              minHeight: 56,
            }}
          >
            I have an account
          </button>
        </div>

        <div style={{ marginTop: 16, fontSize: "0.875rem", color: "#71717a" }}>
          <button type="button" onClick={onTryDemo} style={{ background: "none", border: "none", color: "inherit", fontSize: "inherit", cursor: "pointer", padding: 0 }}>
            Try the demo
          </button>
          {!isNative ? (
            <>
              {" "}•{" "}
              <button type="button" onClick={onCreateAccount} style={{ background: "none", border: "none", color: "inherit", fontSize: "inherit", cursor: "pointer", padding: 0 }}>
                Create an account
              </button>
            </>
          ) : null}
        </div>
      </section>

      {/* ── Ghost progress chart watermark (iOS only) ───────────────────── */}
      {isNative && <GhostProgressChart />}

      {/* ── Sprint card preview + feature copy (web only, not iOS) ───────── */}
      {!isNative && (
        <>
          {/* Sprint card section */}
          <section aria-label="Live sprint preview" style={{ maxWidth: 1152, margin: "0 auto", padding: "0 24px 112px" }}>
            <div style={{ position: "relative", maxWidth: 1024, margin: "0 auto" }}>
              {/* Glow backdrop */}
              <div
                aria-hidden="true"
                style={{ position: "absolute", inset: 0, pointerEvents: "none", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <div style={{ width: "62%", height: "56%", borderRadius: "9999px", background: "radial-gradient(ellipse at center, rgba(59,130,246,0.22) 0%, rgba(51,65,85,0.14) 42%, rgba(51,65,85,0) 72%)", filter: "blur(48px)" }} />
              </div>

              {/* Desktop card — ≥ 1024px */}
              <div className="landing-sprint-desktop" style={{ position: "relative" }}>
                <SprintCardDesktop />
              </div>

              {/* Mobile card — < 1024px */}
              <div className="landing-sprint-mobile" style={{ position: "relative" }}>
                <SprintCardMobile />
              </div>
            </div>
          </section>

          {/* Feature blocks */}
          <section aria-label="Features" style={{ maxWidth: 1152, margin: "0 auto", padding: "0 24px 128px", display: "grid", gap: 112 }}>
            <FeatureBlock title="Define your phase" description="Create focused cut, bulk, or maintenance sprints with clear pacing and targets." />
            <FeatureBlock title="Track plan vs reality" description="Visualize actual progress against projections so you always know where you stand." />
            <FeatureBlock title="Recalibrate intelligently" description="Adjust calorie targets dynamically when you're ahead or behind pace." />
            <FeatureBlock title="Plan the long game" description="Map future phases and review historical trends to improve execution over time." />
          </section>

          {/* CTA footer */}
          <footer style={{ borderTop: "1px solid #27272a", padding: "80px 24px", textAlign: "center" }}>
            <h2 style={{ margin: 0, fontSize: "1.875rem", fontWeight: 600 }}>Take control of your next phase</h2>
            <div style={{ marginTop: 32 }}>
              <button
                type="button"
                onClick={onContinueLocally}
                style={{
                  background: `linear-gradient(135deg, ${brand.accent.brand} 0%, ${brand.accent.primary} 100%)`,
                  color: "#0f172a",
                  border: "1px solid rgba(148,163,184,0.45)",
                  borderRadius: 12,
                  padding: "12px 40px",
                  fontSize: "1.125rem",
                  fontWeight: 700,
                  cursor: "pointer",
                  minHeight: 56,
                }}
              >
                Build my plan
              </button>
            </div>
            <nav
              aria-label="Legal"
              style={{
                marginTop: 48,
                display: "flex",
                gap: 24,
                justifyContent: "center",
                flexWrap: "wrap",
                fontSize: "0.875rem",
                color: "rgba(229,231,235,0.55)",
              }}
            >
              <a href="/privacy" style={{ color: "inherit", textDecoration: "none" }}>Privacy Policy</a>
              <a href="/terms" style={{ color: "inherit", textDecoration: "none" }}>Terms</a>
              <a href="/support" style={{ color: "inherit", textDecoration: "none" }}>Support</a>
              <span>© {new Date().getFullYear()} WeightPlanner Pro</span>
            </nav>
          </footer>
        </>
      )}

      <style>{`
        /* Sprint card responsive visibility */
        .landing-sprint-desktop { display: block; }
        .landing-sprint-mobile { display: none; }
        @media (max-width: 1023px) {
          .landing-sprint-desktop { display: none !important; }
          .landing-sprint-mobile { display: block !important; }
        }

        /* Hero responsive overrides */
        @media (max-width: 640px) {
          .landing-hero { padding-top: 56px !important; padding-bottom: 44px !important; }
          .landing-logo-wrap { width: 154px !important; height: 154px !important; margin-bottom: 16px !important; }
          .landing-hero-title { font-size: clamp(2.25rem, 10vw, 2.9rem) !important; }
          .landing-hero-copy { margin-top: 18px !important; font-size: 1.01rem !important; line-height: 1.45 !important; }
          .landing-hero-cta { margin-top: 26px !important; gap: 12px !important; }
        }

        @media (max-width: 640px) {
          .landing-hero-cta .landing-cta-btn {
            flex: 1 1 calc(50% - 6px) !important;
            width: calc(50% - 6px) !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
        }
      `}</style>
    </main>
  );
}

function FeatureBlock({ title, description }: { title: string; description: string }) {
  return (
    <div style={{ textAlign: "center", maxWidth: 672, margin: "0 auto" }}>
      <h2 style={{ margin: 0, fontSize: "1.5rem", fontWeight: 600 }}>{title}</h2>
      <p style={{ margin: "16px 0 0", fontSize: "1.125rem", color: "#a1a1aa", lineHeight: 1.55 }}>{description}</p>
    </div>
  );
}
